<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});
const emit = defineEmits(['navigate']);

const query = ref('');
const selectedId = ref('');
const stageFilter = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const pathValue = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => pathValue(value, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const valuesAt = (value, paths) =>
  paths.flatMap(path => asArray(pathValue(value, path)));
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrl = image => {
  if (typeof image === 'string') return image;
  if (!image || typeof image !== 'object') return '';

  return firstValue(image, [
    'url',
    'file_url',
    'image_url',
    'cover_url',
    'preview_url',
  ]);
};
const money = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    return value.formatted || value.display || money(value.amount ?? value.value);
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
};
const shortDate = value => {
  if (!value) return 'Não retornada pela API';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR').format(date);
};
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/conclu|entreg|finaliz/.test(status)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (/doc|assin/.test(status)) return 'bg-blue-50 text-blue-700';
  if (/cancel|recus/.test(status)) return 'bg-rose-50 text-rose-700';
  return 'bg-violet-50 text-[#5a24c7]';
};
const stageIndex = status => {
  const normalized = String(status || '').toLocaleLowerCase('pt-BR');
  if (/conclu|entreg|finaliz/.test(normalized)) return 4;
  if (/assin/.test(normalized)) return 3;
  if (/doc/.test(normalized)) return 2;
  if (/revis|negoci/.test(normalized)) return 1;
  return 0;
};
const participantFrom = (source, paths, role) => {
  const candidate = paths
    .map(path => pathValue(source, path))
    .find(item => firstValue(item, ['name', 'full_name', 'contact.name']));
  const name = firstValue(candidate, ['name', 'full_name', 'contact.name']);

  return name
    ? {
        avatar: imageUrl(
          firstValue(candidate, [
            'avatar_url',
            'thumbnail',
            'contact.thumbnail',
            'user.avatar_url',
          ])
        ),
        initials: initials(name),
        name,
        role,
      }
    : null;
};
const partyParticipant = (source, roles, role) => {
  const participant = valuesAt(source, [
    'participants',
    'party_details',
    'parties',
  ]).find(item =>
    roles.some(expected =>
      String(
        firstValue(item, ['role', 'party_role', 'party_role_label'])
      )
        .toLocaleLowerCase('pt-BR')
        .includes(expected)
    )
  );

  if (!participant) return null;
  const name = firstValue(participant, [
    'name',
    'contact.name',
    'company.name',
    'representative.name',
  ]);

  return name
    ? {
        avatar: imageUrl(
          firstValue(participant, [
            'avatar_url',
            'thumbnail',
            'contact.thumbnail',
          ])
        ),
        initials: initials(name),
        name,
        role,
      }
    : null;
};

const rows = computed(() =>
  props.records
    .map(record => {
      const source = record?.source || record || {};
      const buyer =
        participantFrom(
          source,
          ['buyer', 'client', 'contact', 'purchaser', 'lead'],
          'Comprador(a)'
        ) || partyParticipant(source, ['comprador', 'buyer'], 'Comprador(a)');
      const seller =
        participantFrom(
          source,
          ['seller', 'owner', 'responsible', 'broker'],
          'Vendedor(a)'
        ) || partyParticipant(source, ['vendedor', 'seller'], 'Vendedor(a)');
      const manager =
        participantFrom(
          source,
          ['manager', 'manager_user', 'responsible_manager', 'assignee'],
          'Gerente responsável'
        ) || seller;
      const id = String(record?.id ?? source.id ?? '');

      return {
        amount:
          record?.amount ||
          money(
            firstValue(source, [
              'contracted_total',
              'financial.contracted_total',
              'total_amount',
              'amount',
              'value',
            ])
          ),
        buyer,
        id,
        image: imageUrl(
          firstValue(source, [
            'imovel.cover_url',
            'imovel.image_url',
            'property.cover_url',
            'property.image_url',
            'listing.cover_url',
            'listing.image_url',
            'image_url',
          ])
        ),
        manager,
        reference: firstValue(source, ['reference', 'code', 'transaction_code']),
        seller,
        source,
        status:
          record?.status ||
          firstValue(source, ['sales_stage_label', 'status_label', 'status']) ||
          'Status não retornado',
        subtitle:
          record?.subtitle ||
          firstValue(source, [
            'address.endereco',
            'address.neighborhood',
            'imovel.endereco',
            'property.address',
            'reference',
          ]) ||
          'Endereço não retornado',
        title:
          record?.title ||
          firstValue(source, [
            'title',
            'property.title',
            'imovel.titulo',
            'reference',
            'id',
          ]) ||
          'Venda não retornada',
      };
    })
    .filter(row => row.id)
);
const stageOptions = computed(() =>
  [...new Set(rows.value.map(row => row.status).filter(Boolean))].sort()
);
const filteredRows = computed(() => {
  const normalizedQuery = query.value.trim().toLocaleLowerCase('pt-BR');

  return rows.value.filter(row => {
    const matchesStage = !stageFilter.value || row.status === stageFilter.value;
    const matchesQuery =
      !normalizedQuery ||
      `${row.title} ${row.subtitle} ${row.buyer?.name || ''} ${row.manager?.name || ''}`
        .toLocaleLowerCase('pt-BR')
        .includes(normalizedQuery);

    return matchesStage && matchesQuery;
  });
});
const selected = computed(() =>
  filteredRows.value.find(row => row.id === selectedId.value) || null
);
const roster = computed(() => {
  const people = [];

  rows.value.forEach(row => {
    const person = row.manager || row.seller || row.buyer;
    if (person && !people.some(item => item.name === person.name)) {
      people.push(person);
    }
  });

  return people.slice(0, 4);
});
const rosterSlots = computed(() => [
  ...roster.value.map(person => ({
    ...person,
    id: `participant-${person.name}`,
    unavailable: false,
  })),
  ...Array.from({ length: Math.max(0, 4 - roster.value.length) }, (_, index) => ({
    id: `participant-unavailable-${index}`,
    unavailable: true,
  })),
]);
const tableEmptySlots = computed(() => {
  if (query.value || stageFilter.value) return 0;

  return Math.max(0, 8 - filteredRows.value.length);
});
const metricSlots = computed(() => {
  const fallbackIcons = [
    'i-lucide-chart-no-axes-combined',
    'i-lucide-search',
    'i-lucide-package-check',
    'i-lucide-wallet-cards',
  ];
  const fallbackTones = [
    'text-violet-600',
    'text-orange-500',
    'text-emerald-600',
    'text-blue-600',
  ];
  const metrics = props.metrics.slice(0, 4).map((metric, index) => ({
    helper: metric.helper || `${props.total || rows.value.length} vendas`,
    icon: metric.icon || fallbackIcons[index],
    id: metric.label || `metric-${index}`,
    label: metric.label || 'Métrica não retornada',
    tone: fallbackTones[index],
    value: metric.value ?? '—',
  }));

  return [
    ...metrics,
    ...Array.from({ length: Math.max(0, 4 - metrics.length) }, (_, index) => ({
      helper: 'Não retornada pela API',
      icon: fallbackIcons[metrics.length + index],
      id: `metric-unavailable-${index}`,
      label: 'Métrica indisponível',
      tone: 'text-[#9ca5b6]',
      value: '—',
    })),
  ];
});
const selectedParticipants = computed(() => {
  if (!selected.value) return [];

  return [selected.value.buyer, selected.value.seller, selected.value.manager].map(
    (participant, index) =>
      participant || {
        id: `participant-unavailable-${index}`,
        role: ['Comprador(a)', 'Vendedor(a)', 'Gerente responsável'][index],
        unavailable: true,
      }
  );
});
const selectedStage = computed(() => stageIndex(selected.value?.status));
const selectedFacts = computed(() => {
  const source = selected.value?.source || {};

  return [
    {
      label: 'Valor da venda',
      value: selected.value?.amount || '—',
    },
    {
      label: 'Comissão',
      value: money(
        firstValue(source, [
          'commission_amount',
          'commission.amount',
          'financial.commission_amount',
        ])
      ),
    },
    {
      label: 'Previsão de assinatura',
      value: shortDate(
        firstValue(source, [
          'expected_signature_at',
          'signature_due_at',
          'estimated_signature_at',
        ])
      ),
    },
  ];
});
const selectedPendingItems = computed(() =>
  valuesAt(selected.value?.source || {}, [
    'pending_items',
    'pendencies',
    'checklist_items',
  ])
    .filter(item => !/done|completed|conclu/i.test(String(item.status || '')))
    .map((item, index) => ({
      id: String(item.id || `${item.title || item.name || 'pending'}-${index}`),
      status: firstValue(item, ['status_label', 'status'], 'Pendente'),
      title: firstValue(item, ['title', 'name', 'label'], 'Pendência sem título'),
    }))
    .slice(0, 3)
);
const pendingEmptySlots = computed(() =>
  Math.max(0, 3 - selectedPendingItems.value.length)
);
const selectedActivities = computed(() => {
  const events = valuesAt(selected.value?.source || {}, [
    'timeline',
    'events',
    'history',
  ]);
  const sourceEvents = events.length ? events : props.history;

  return sourceEvents.slice(0, 4).map((event, index) => ({
    actor:
      firstValue(event, [
        'actor.name',
        'actor_name',
        'user.name',
        'author.name',
      ]) || 'Sistema',
    avatar: imageUrl(
      firstValue(event, [
        'actor.avatar_url',
        'actor_avatar_url',
        'user.avatar_url',
        'author.avatar_url',
      ])
    ),
    date:
      event.date ||
      shortDate(
        firstValue(event, [
          'occurred_at',
          'created_at',
          'updated_at',
          'at',
        ])
      ),
    id: String(event.id || event.sequence || `activity-${index}`),
    label:
      firstValue(event, [
        'label',
        'title',
        'body',
        'action',
        'event_type',
        'notes',
      ]) || 'Atualização registrada',
  }));
});
const activityEmptySlots = computed(() =>
  Math.max(0, 4 - selectedActivities.value.length)
);
const previewEmptyMessage = computed(() =>
  props.preview
    ? 'A visualização solicitada não usa dados simulados. Nenhuma venda real foi retornada.'
    : 'Nenhuma venda foi retornada pela API.'
);

watch(
  filteredRows,
  nextRows => {
    if (!nextRows.some(row => row.id === selectedId.value)) {
      selectedId.value = nextRows[0]?.id || '';
    }
  },
  { immediate: true }
);

const select = row => {
  selectedId.value = row.id;
};
const open = screen => {
  if (selected.value?.id) emit('navigate', { id: selected.value.id, screen });
};
</script>

<template>
  <main class="mx-auto w-full max-w-[1470px] translate-x-[5px] px-4 py-3 text-[#101958]">
    <header data-imudei-visual-id="sales-dashboard-header">
      <h1 class="text-[27px] font-bold tracking-[-0.035em]">Vendas</h1>
      <p class="mt-1 text-[10px] text-[#66739b]">
        Acompanhe o funil e gerencie suas vendas com os dados retornados pela API.
      </p>
    </header>

    <section class="mt-4 grid grid-cols-[minmax(0,1fr)_600px] gap-4">
      <section class="min-w-0">
        <div class="flex h-[108px] items-center gap-[30px]">
          <article
            v-for="person in rosterSlots"
            :key="person.id"
            class="-mt-2 w-[74px] text-center"
          >
              <span
                class="mx-auto grid size-[66px] place-items-center overflow-hidden rounded-full border-2 border-[#d9cbff] bg-[#f0eaff] text-[10px] font-bold text-[#5a24c7] ring-2 ring-[#f9e7ff]"
              >
                <img
                  v-if="!person.unavailable && person.avatar"
                  :src="person.avatar"
                  :alt="person.name"
                  class="h-full w-full object-cover"
                />
                <span v-else-if="person.unavailable" class="i-lucide-user-round-x size-5 text-[#9ca5b6]" />
                <template v-else>{{ person.initials }}</template>
              </span>
              <strong class="mt-2 block truncate text-[9px]">{{ person.unavailable ? 'Participante' : person.name }}</strong>
              <small class="mt-0.5 block truncate text-[8px] text-[#7480a0]">{{ person.unavailable ? 'Não retornado' : 'Participante ativo' }}</small>
          </article>
          <article
            class="-mt-2 w-[72px] text-center text-[#7480a0]"
            aria-label="Ranking indisponível"
          >
            <span class="mx-auto grid size-[66px] place-items-center rounded-full border border-dashed border-[#b9add4] bg-white text-[#8066b7]">
              <span class="i-lucide-trophy size-7" />
            </span>
            <strong class="mt-2 block text-[8px]">Ranking</strong>
            <small class="mt-0.5 block text-[7px]">Indisponível</small>
          </article>
          <article
            class="ml-auto mr-[5px] -mb-[6px] -mt-[14px] flex h-[126px] w-[286px] items-center gap-3 rounded-2xl border border-[#e9e2f6] bg-gradient-to-r from-[#fbf9ff] to-[#f8fcff] px-4"
          >
            <span class="grid size-10 place-items-center rounded-lg bg-violet-100 text-[#5a24c7]">
              <span class="i-lucide-sparkles size-[18px]" />
            </span>
            <span class="min-w-0 flex-1">
              <strong class="block text-[10px]">Missão do dia</strong>
              <small class="mt-2 block text-[8px] leading-4 text-[#6b7696]">
                Regras de missões e recompensas não foram retornadas pela API.
              </small>
            </span>
          </article>
        </div>

        <div data-imudei-visual-id="sales-dashboard-metrics" class="mt-4 grid grid-cols-[1.1fr_1.1fr_1.1fr_.8fr] gap-3">
          <article
            v-for="metric in metricSlots"
            :key="metric.id"
            class="flex h-[84px] items-center gap-3 rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-[0_3px_12px_rgba(45,25,90,0.025)]"
          >
            <span
              class="grid size-12 place-items-center rounded-full bg-[#f7f4fb]"
              :class="metric.tone"
            >
              <span :class="metric.icon" class="size-5" />
            </span>
            <span class="min-w-0">
              <small class="block truncate text-[9px] text-[#647094]">{{ metric.label }}</small>
              <strong class="mt-1 block truncate text-[19px]">{{ metric.value }}</strong>
              <small class="mt-1 block truncate text-[8px] text-[#7b86a1]">{{ metric.helper }}</small>
            </span>
          </article>
        </div>

        <section class="mt-[14px]">
          <div data-imudei-visual-id="sales-dashboard-filters" class="flex gap-3">
            <label class="flex h-10 flex-1 items-center gap-2 rounded-lg border border-[#e2ddea] bg-white px-3 text-[9px] text-[#7883a2]">
              <span class="i-lucide-search size-4" />
              <input
                v-model="query"
                class="w-full border-0 bg-transparent p-0 text-[9px] text-[#34406d] outline-none placeholder:text-[#7883a2]"
                placeholder="Buscar por imóvel, cliente ou gerente..."
              />
            </label>
            <label class="flex h-10 w-[140px] items-center gap-2 rounded-lg border border-[#e2ddea] bg-white px-3 text-[9px] text-[#59678d]">
              Etapa:
              <select
                v-model="stageFilter"
                class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[9px] font-semibold outline-none"
              >
                <option value="">Todas</option>
                <option v-for="stage in stageOptions" :key="stage" :value="stage">
                  {{ stage }}
                </option>
              </select>
            </label>
            <button
              class="grid size-10 place-items-center rounded-lg border border-[#e2ddea] bg-white text-[#8b76b5]"
              type="button"
              disabled
              aria-label="Filtros adicionais indisponíveis"
              title="Filtros adicionais indisponíveis"
            >
              <span class="i-lucide-funnel size-4" />
            </button>
          </div>
          <div class="mt-4 grid grid-cols-[1.55fr_.9fr_.87fr_.76fr_.58fr] gap-3 px-3 text-[8px] text-[#7480a0]">
            <span>Imóvel</span>
            <span>Cliente</span>
            <span>Gerente</span>
            <span>Valor</span>
            <span>Etapa</span>
          </div>
          <div v-if="filteredRows.length" data-imudei-visual-id="sales-dashboard-table" class="mt-2 space-y-0">
            <button
              v-for="row in filteredRows"
              :key="row.id"
              type="button"
              class="grid min-h-[51px] w-full grid-cols-[1.55fr_.9fr_.87fr_.76fr_.58fr] items-center gap-3 rounded-xl border border-[#eeeaf4] px-3 text-left"
              :class="selected?.id === row.id ? 'bg-violet-50' : 'bg-white hover:bg-[#fcfbff]'"
              @click="select(row)"
            >
              <span class="flex min-w-0 items-center gap-3">
                <span class="grid h-[48px] w-[80px] shrink-0 place-items-center overflow-hidden rounded-lg bg-[#f2eef8] text-[#927cbf]">
                  <img
                    v-if="row.image"
                    :src="row.image"
                    :alt="row.title"
                    class="h-full w-full object-cover"
                  />
                  <span v-else class="i-lucide-building-2 size-4" />
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-[9px]">{{ row.title }}</strong>
                  <small class="mt-1 block truncate text-[8px] text-[#7480a0]">{{ row.subtitle }}</small>
                </span>
              </span>
              <span class="flex min-w-0 items-center gap-2">
                <span
                  v-if="row.buyer"
                  class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#f1eaff] text-[7px] font-bold text-[#5a24c7]"
                >
                  <img
                    v-if="row.buyer.avatar"
                    :src="row.buyer.avatar"
                    :alt="row.buyer.name"
                    class="h-full w-full object-cover"
                  />
                  <template v-else>{{ row.buyer.initials }}</template>
                </span>
                <small class="truncate text-[8px]">{{ row.buyer?.name || 'Não retornado' }}</small>
              </span>
              <span class="flex min-w-0 items-center gap-2">
                <span
                  v-if="row.manager"
                  class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#eaf5ff] text-[7px] font-bold text-[#276aaf]"
                >
                  <img
                    v-if="row.manager.avatar"
                    :src="row.manager.avatar"
                    :alt="row.manager.name"
                    class="h-full w-full object-cover"
                  />
                  <template v-else>{{ row.manager.initials }}</template>
                </span>
                <small class="truncate text-[8px]">{{ row.manager?.name || 'Não retornado' }}</small>
              </span>
              <strong class="truncate text-[9px]">{{ row.amount }}</strong>
              <span class="flex min-w-0 items-center gap-2 text-[8px]" :class="statusTone(row.status)">
                <span class="size-2 shrink-0 rounded-full bg-current" />
                <span class="truncate">{{ row.status }}</span>
              </span>
            </button>
            <div
              v-for="slot in tableEmptySlots"
              :key="`sale-unavailable-${slot}`"
              class="grid min-h-[51px] w-full grid-cols-[1.55fr_.9fr_.87fr_.76fr_.58fr] items-center gap-3 rounded-xl border border-[#eeeaf4] bg-[#faf9fc] px-3 text-[#8790a7]"
            >
              <span class="flex min-w-0 items-center gap-3">
                <span class="grid h-[48px] w-[80px] shrink-0 place-items-center rounded-lg bg-[#f2eef8] text-[#927cbf]">
                  <span class="i-lucide-building-2 size-4" />
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-[9px] text-[#68749a]">Nenhuma venda adicional retornada</strong>
                  <small class="mt-1 block truncate text-[8px]">Dados não retornados pela API.</small>
                </span>
              </span>
              <small class="text-[8px]">—</small>
              <small class="text-[8px]">—</small>
              <small class="text-[8px]">—</small>
              <span class="text-[8px]">Sem dados</span>
            </div>
          </div>
          <div v-else data-imudei-visual-id="sales-dashboard-table" class="mt-2 grid min-h-[416px] place-items-center rounded-xl border border-dashed border-[#e1dce9] bg-white p-6 text-center">
            <span>
              <span class="mx-auto grid size-10 place-items-center rounded-full bg-[#f4f1f7] text-[#8c95aa]">
                <span class="i-lucide-search-x size-5" />
              </span>
              <strong class="mt-3 block text-[10px] text-[#566488]">Nenhuma venda encontrada</strong>
              <small class="mt-1 block text-[8px] text-[#8790a7]">{{ previewEmptyMessage }}</small>
            </span>
          </div>
          <footer data-imudei-visual-id="sales-dashboard-footer" class="mt-5 flex items-center justify-between text-[9px] text-[#66739b]">
            <span>{{ filteredRows.length }} de {{ total || rows.length }} vendas reais</span>
            <span v-if="query || stageFilter">Filtros aplicados</span>
            <span v-else>Dados retornados pela API</span>
          </footer>
        </section>
      </section>

      <aside data-imudei-visual-id="sales-dashboard-detail" class="-mt-[85px] flex min-h-[calc(100vh-87px)] flex-col overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white shadow-[0_3px_14px_rgba(45,25,90,0.035)]">
        <template v-if="selected">
          <header class="flex items-start gap-4 px-5 py-[17px]">
            <span class="grid size-[116px] shrink-0 place-items-center overflow-hidden rounded-xl bg-[#f2eef8] text-[#927cbf]">
              <img
                v-if="selected.image"
                :src="selected.image"
                :alt="selected.title"
                class="h-full w-full object-cover"
              />
              <span v-else class="i-lucide-building-2 size-7" />
            </span>
            <span class="min-w-0 flex-1">
              <strong class="block truncate text-[16px]">{{ selected.title }}</strong>
              <small class="mt-2 block truncate text-[9px] text-[#68749a]">{{ selected.subtitle }}</small>
              <div class="mt-4 flex items-center gap-3">
                <span class="text-[9px] font-semibold text-[#59678d]">{{ selected.reference || `Venda #${selected.id}` }}</span>
                <span class="rounded-full px-2 py-1 text-[8px]" :class="statusTone(selected.status)">{{ selected.status }}</span>
              </div>
            </span>
            <button
              type="button"
              class="i-lucide-x size-5 text-[#69759b]"
              aria-label="Fechar visualização"
              @click="selectedId = ''"
            />
          </header>

          <section class="border-t border-[#eeeaf4] px-5 py-[26px]">
            <div class="grid grid-cols-3 gap-3">
              <div v-for="participant in selectedParticipants" :key="participant.role">
                <small class="block text-[8px] text-[#70809e]">{{ participant.role }}</small>
                <span v-if="!participant.unavailable" class="mt-2 flex min-w-0 items-center gap-2">
                  <span class="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#f0eaff] text-[7px] font-bold text-[#5a24c7]">
                    <img
                      v-if="participant.avatar"
                      :src="participant.avatar"
                      :alt="participant.name"
                      class="h-full w-full object-cover"
                    />
                    <template v-else>{{ participant.initials }}</template>
                  </span>
                  <strong class="truncate text-[8px]">{{ participant.name }}</strong>
                </span>
                <span v-else class="mt-2 block text-[8px] text-[#9aa2b6]">Não retornado</span>
              </div>
            </div>
          </section>

          <section class="mx-5 grid grid-cols-3 divide-x divide-[#eeeaf4] rounded-xl border border-[#eeeaf4] py-[21px]">
            <span v-for="fact in selectedFacts" :key="fact.label" class="min-w-0 px-3">
              <small class="block truncate text-[8px] text-[#70809e]">{{ fact.label }}</small>
              <strong class="mt-2 block truncate text-[11px]">{{ fact.value }}</strong>
            </span>
          </section>

          <section class="mt-4 grid grid-cols-[minmax(0,1fr)_198px] border-t border-[#eeeaf4]">
            <div class="min-w-0">
              <section class="px-5 pb-0 pt-3">
                <header class="flex items-center justify-between">
                  <h2 class="text-[10px] font-bold">Jornada da venda</h2>
                  <small class="text-[8px] text-[#7480a0]">{{ selected.status }}</small>
                </header>
                <div class="relative mt-4 flex items-start justify-between">
                  <span class="absolute left-7 right-7 top-4 h-px bg-[#e5e0ed]" />
                  <span
                    v-for="(stage, index) in ['Rascunho', 'Em revisão', 'Documentação', 'Assinatura', 'Concluída']"
                    :key="stage"
                    class="relative z-10 flex w-1/5 flex-col items-center text-center"
                  >
                    <span
                      class="grid size-8 place-items-center rounded-full border bg-white text-[8px] font-bold"
                      :class="index <= selectedStage ? 'border-[#6a2ee1] text-[#5a24c7]' : 'border-[#ded9e7] text-[#9ca5b6]'"
                    >
                      {{ index + 1 }}
                    </span>
                    <strong class="mt-2 text-[7px]" :class="index === selectedStage ? 'text-[#5a24c7]' : 'text-[#71809d]'">{{ stage }}</strong>
                  </span>
                </div>
              </section>

              <div class="border-t border-[#eeeaf4] px-5 pb-4 pt-3">
              <div class="grid min-h-[83px] grid-cols-2 gap-3">
                <section>
                  <header class="flex items-center justify-between">
                    <h2 class="text-[10px] font-bold">Conquistas desta venda</h2>
                    <span class="i-lucide-award size-4 text-[#8b60d3]" />
                  </header>
                  <div class="mt-3 flex gap-2">
                    <span class="grid size-10 place-items-center rounded-xl bg-violet-50 text-[#7b48d6]">
                      <span class="i-lucide-award size-5" />
                    </span>
                    <span class="min-w-0 text-[8px] leading-4 text-[#78839e]">Conquistas não retornadas pela API.</span>
                  </div>
                </section>
                <aside class="rounded-xl bg-[#faf9fc] p-3">
                  <h3 class="text-[8px] font-bold">Próxima recompensa</h3>
                  <p class="mt-2 text-[8px] leading-4 text-[#78839e]">Dados de pontos e recompensa indisponíveis.</p>
                </aside>
              </div>
              <div class="mt-3 flex min-h-[55px] items-center justify-between gap-3 rounded-xl border border-[#eadcff] bg-[#f7f0ff] px-3 py-2 text-[#5a24c7]">
                <span class="flex min-w-0 items-center gap-2">
                  <span class="i-lucide-sparkles size-4 shrink-0" />
                  <span class="min-w-0">
                    <strong class="block text-[9px]">Missões e pontos indisponíveis</strong>
                    <small class="mt-0.5 block text-[8px] text-[#8066b7]">Metas não retornadas pela API.</small>
                  </span>
                </span>
                <span class="shrink-0 rounded-lg border border-[#decaf8] bg-white px-2 py-1 text-[8px] font-semibold">Indisponível</span>
              </div>
              <section class="mt-4">
                <header class="flex items-center justify-between">
                  <h2 class="text-[10px] font-bold">Pendências</h2>
                  <span class="rounded-full bg-violet-50 px-2 py-1 text-[8px] text-[#5a24c7]">{{ selectedPendingItems.length || '—' }}</span>
                </header>
                <div class="mt-3 space-y-2">
                  <div
                    v-for="item in selectedPendingItems"
                    :key="item.id"
                    class="flex items-center gap-2 rounded-lg border border-[#eeeaf4] px-3 py-2"
                  >
                    <span class="grid size-6 place-items-center rounded-md bg-violet-50 text-[#5a24c7]">
                      <span class="i-lucide-clipboard-list size-3" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <strong class="block truncate text-[8px]">{{ item.title }}</strong>
                      <small class="mt-1 block text-[7px] text-[#78839e]">{{ item.status }}</small>
                    </span>
                  </div>
                  <div
                    v-for="slot in pendingEmptySlots"
                    :key="`pending-unavailable-${slot}`"
                    class="flex min-h-[42px] items-center gap-2 rounded-lg border border-[#eeeaf4] bg-[#faf9fc] px-3 text-[#8790a7]"
                  >
                    <span class="grid size-6 place-items-center rounded-md bg-[#f1eef5] text-[#9aa3b7]">
                      <span class="i-lucide-clipboard-list size-3" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <strong class="block truncate text-[8px] text-[#68749a]">Nenhuma pendência retornada</strong>
                      <small class="mt-1 block text-[7px]">Dados não retornados pela API.</small>
                    </span>
                  </div>
                </div>
              </section>
              </div>
            </div>

            <aside class="min-w-0 border-l border-[#eeeaf4] px-4 py-4">
              <header class="flex items-center justify-between">
                <h2 class="text-[10px] font-bold">Atividades recentes</h2>
                <span class="text-[8px] text-[#5a24c7]">{{ selectedActivities.length || '—' }}</span>
              </header>
              <div class="mt-3 space-y-2">
                <article v-for="activity in selectedActivities" :key="activity.id" class="flex min-h-[74px] gap-2 rounded-xl border border-[#eeeaf4] p-2">
                  <span class="grid size-7 shrink-0 place-items-center overflow-hidden rounded-full bg-[#f0eaff] text-[7px] font-bold text-[#5a24c7]">
                    <img
                      v-if="activity.avatar"
                      :src="activity.avatar"
                      :alt="activity.actor"
                      class="h-full w-full object-cover"
                    />
                    <template v-else>{{ initials(activity.actor) }}</template>
                  </span>
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-[8px]">{{ activity.actor }}</strong>
                    <small class="mt-1 block line-clamp-2 text-[8px] leading-4 text-[#68749a]">{{ activity.label }}</small>
                  </span>
                  <small class="text-[7px] text-[#8a94a9]">{{ activity.date }}</small>
                </article>
                <article
                  v-for="slot in activityEmptySlots"
                  :key="`activity-unavailable-${slot}`"
                  class="flex gap-2 rounded-xl border border-[#eeeaf4] bg-[#faf9fc] p-2 text-[#8790a7]"
                  :class="selectedActivities.length || slot > 1 ? 'min-h-[74px]' : 'min-h-[94px]'"
                >
                  <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f1eef5] text-[#9aa3b7]">
                    <span class="i-lucide-clock-3 size-3" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-[8px] text-[#68749a]">Atividade não retornada</strong>
                    <small class="mt-1 block text-[8px] leading-4">Sem outro evento no histórico da API.</small>
                  </span>
                </article>
              </div>
              <span v-if="activityEmptySlots" class="mt-3 flex h-8 items-center justify-center rounded-lg border border-[#ded4f8] text-[8px] font-semibold text-[#8066b7]">
                Histórico indisponível
              </span>
            </aside>
          </section>

          <footer class="mt-auto flex gap-3 border-t border-[#eeeaf4] bg-[#fcfbff] px-4 py-[14px]">
            <button
              type="button"
              class="flex h-[35px] flex-1 items-center justify-center gap-2 rounded-lg border border-[#cbb8fa] text-[9px] font-semibold text-[#5a24c7]"
              @click="open('detail')"
            >
              <span class="i-lucide-eye size-4" />Abrir detalhes
            </button>
            <button
              type="button"
              class="flex h-[35px] flex-1 items-center justify-center gap-2 rounded-lg border border-[#cbb8fa] text-[9px] font-semibold text-[#5a24c7]"
              @click="open('edit')"
            >
              <span class="i-lucide-pencil size-4" />Editar
            </button>
            <span class="flex h-[35px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#f0edf5] text-[9px] font-semibold text-[#8c95aa]">
              <span class="i-lucide-route size-4" />Ações indisponíveis
            </span>
          </footer>
        </template>

        <div v-else class="grid min-h-[720px] place-items-center p-8 text-center">
          <span>
            <span class="mx-auto grid size-12 place-items-center rounded-full bg-[#f1eef5] text-[#9aa3b7]">
              <span class="i-lucide-panel-right-open size-5" />
            </span>
            <strong class="mt-4 block text-[11px] text-[#62708e]">Visualização rápida indisponível</strong>
            <small class="mt-2 block max-w-[230px] text-[8px] leading-4 text-[#8790a7]">
              Selecione uma venda retornada pela API para ver os detalhes.
            </small>
          </span>
        </div>
      </aside>
    </section>
  </main>
</template>
